<app-drawer class="w-full min-w-0 max-w-full">
  <app-drawer-header [title]="customerName()" [subtitle]="customerCode()" icon="person" (close)="close()" />

  <!-- Loading State -->
  @if (loading()) {
    <div class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <span class="text-secondary">Cargando datos del cliente...</span>
    </div>
  }

  <!-- Error State -->
  @if (error() && !loading()) {
    <div class="error-container">
      <mat-icon class="icon-size-12">error_outline</mat-icon>
      <span>{{ error() }}</span>
      <button mat-stroked-button (click)="retry()">
        <mat-icon>refresh</mat-icon>
        Reintentar
      </button>
    </div>
  }

  <!-- Content -->
  @if (customer() && !loading() && !error()) {
    <div class="drawer-body">
      <!-- Basic Info Section -->
      <app-form-section title="Información Básica" icon="badge" color="blue">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Código</span>
            <span class="info-value">{{ customerCode() || 'Sin código' }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Estado</span>
            <span class="status-badge" [class.status-active]="isActive()" [class.status-inactive]="!isActive()">
              {{ isActive() ? 'Activo' : 'Inactivo' }}
            </span>
          </div>

          <div class="info-item full-width">
            <span class="info-label">Nombre</span>
            <span class="info-value">{{ customer()?.name || 'Sin nombre' }}</span>
          </div>

          @if (customer()?.commercialName) {
            <div class="info-item full-width">
              <span class="info-label">Nombre Comercial</span>
              <span class="info-value">{{ customer()?.commercialName }}</span>
            </div>
          }

          @if (customer()?.businessPartnerType) {
            <div class="info-item">
              <span class="info-label">Tipo</span>
              <span class="info-value">{{ customer()?.businessPartnerType?.name }}</span>
            </div>
          }

          @if (businessPartnerGroup()) {
            <div class="info-item">
              <span class="info-label">Grupo</span>
              <span class="info-value">{{ businessPartnerGroup()?.name }}</span>
            </div>
          }

          @if (businessPartnerSubGroup()) {
            <div class="info-item">
              <span class="info-label">Subgrupo</span>
              <span class="info-value">{{ businessPartnerSubGroup()?.name }}</span>
            </div>
          }

          @if (customer()?.route) {
            <div class="info-item">
              <span class="info-label">Ruta</span>
              <span class="info-value">{{ customer()?.route?.name }}</span>
            </div>
          }

          @if (routeArea()) {
            <div class="info-item">
              <span class="info-label">Área de Ruta</span>
              <span class="info-value">{{ routeArea()?.name }}</span>
            </div>
          }

          @if (customer()?.salesOffice) {
            <div class="info-item">
              <span class="info-label">Oficina de Ventas</span>
              <span class="info-value">{{ customer()?.salesOffice?.name }}</span>
            </div>
          }
        </div>
      </app-form-section>

      <!-- Contact Section: Phones -->
      <app-form-section title="Teléfonos" icon="phone" color="green">
        @if (phones().length > 0) {
          <div class="contact-list">
            @for (phone of phones(); track $index) {
              <div class="contact-item">
                <div class="contact-info">
                  <div class="contact-value">{{ phone.phone }}</div>
                  @if (phone.type) {
                    <div class="contact-type">{{ phone.type?.type }}</div>
                  }
                </div>
                @if (phone.isPrincipal) {
                  <mat-icon matTooltip="Principal" class="text-green-500">star</mat-icon>
                }
              </div>
            }
          </div>
        } @else {
          <div class="empty-section">
            <mat-icon class="mb-2 icon-size-8">phone_disabled</mat-icon>
            <span>Sin teléfonos registrados</span>
          </div>
        }
      </app-form-section>

      <!-- Contact Section: Emails -->
      <app-form-section title="Correos Electrónicos" icon="email" color="orange">
        @if (emails().length > 0) {
          <div class="contact-list">
            @for (email of emails(); track $index) {
              <div class="contact-item">
                <div class="contact-info">
                  <div class="contact-value">{{ email.email }}</div>
                  @if (email.type) {
                    <div class="contact-type">{{ email.type }}</div>
                  }
                </div>
                @if (email.isPrimary) {
                  <mat-icon matTooltip="Principal" class="text-orange-500">star</mat-icon>
                }
              </div>
            }
          </div>
        } @else {
          <div class="empty-section">
            <mat-icon class="mb-2 icon-size-8">mail_outline</mat-icon>
            <span>Sin correos registrados</span>
          </div>
        }
      </app-form-section>

      <!-- Addresses Section -->
      <app-form-section title="Direcciones" icon="location_on" color="red">
        @if (formattedAddresses().length > 0) {
          <div class="contact-list">
            @for (address of formattedAddresses(); track $index) {
              <div class="address-card">
                <div class="address-main">{{ address.formattedAddress }}</div>
                @if (address.locationCodes) {
                  <div class="address-secondary">{{ address.locationCodes }}</div>
                }
                @if (address.reference) {
                  <div class="address-secondary mt-1">Ref: {{ address.reference }}</div>
                }
              </div>
            }
          </div>
        } @else {
          <div class="empty-section">
            <mat-icon class="mb-2 icon-size-8">location_off</mat-icon>
            <span>Sin direcciones registradas</span>
          </div>
        }
      </app-form-section>

      <!-- Territorio Section -->
      @if (customer()?.route) {
        <app-form-section title="Territorio" icon="public" color="indigo">
          <div class="route-chip">
            <mat-icon class="icon-size-4">public</mat-icon>
            {{ customer()?.route?.name }}
          </div>
        </app-form-section>
      }

      <!-- Routes Section (SubRoutes) -->
      <app-form-section title="Rutas Asignadas" icon="route" color="purple">
        @if (subRoutes().length > 0) {
          <div class="flex flex-col gap-3">
            @for (subRoute of subRoutes(); track subRoute._id) {
              <div class="route-frequency-card">
                <div class="route-header">
                  <mat-icon class="icon-size-4 text-primary">route</mat-icon>
                  <span class="route-name">{{ subRoute.name }}</span>
                </div>
                <div class="route-frequency-body">
                  @if ((subRoute.frequencies?.length ?? 0) > 0) {
                    <app-client-frequency-display
                      [subRouteId]="subRoute._id"
                      [subRouteNormalId]="subRoute.id"
                      [businessPartnerId]="customer()?._id ?? ''"
                      [businessPartnerNormalId]="customer()?.id ?? 0"
                      [frequenciesFromThisSubRoute]="subRoute.frequencies ?? []"
                      (refreshRequested)="onFrequencyRefresh()">
                    </app-client-frequency-display>
                  } @else {
                    <span class="no-frequency-badge">Sin frecuencia configurada</span>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="empty-section">
            <mat-icon class="mb-2 icon-size-8">alt_route</mat-icon>
            <span>Sin rutas asignadas</span>
          </div>
        }
      </app-form-section>

      <!-- Geolocation Section -->
      <app-form-section title="Geolocalización" icon="my_location" color="teal">
        <div class="geo-status" [class.geo-valid]="hasValidGeolocation()" [class.geo-invalid]="!hasValidGeolocation()">
          <mat-icon>{{ hasValidGeolocation() ? 'check_circle' : 'cancel' }}</mat-icon>
          <span>
            {{ hasValidGeolocation() ? 'Ubicación válida' : 'Sin ubicación válida' }}
          </span>
        </div>
        @if (hasValidGeolocation()) {
          <div class="info-grid mt-3">
            <div class="info-item">
              <span class="info-label">Latitud</span>
              <span class="info-value">{{ customer()?.geolocation?.lat }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Longitud</span>
              <span class="info-value">{{ customer()?.geolocation?.lng }}</span>
            </div>
          </div>
        }
      </app-form-section>

      <!--
        Credit section. Used to read `customer().credit`, a property that does not
        exist on BusinessPartnerAttributes, so this block could never render.
        Credit is per company: it lives on the `bpCompanies[]` entry, with the
        partner root holding the deprecated pre-multicompany copy.
      -->
      @if (hasCreditInfo()) {
        <app-form-section title="Información de Crédito" icon="account_balance" color="amber">
          <div class="info-grid">
            @if (creditLimit() != null) {
              <div class="info-item">
                <span class="info-label">Límite de Crédito</span>
                <span class="info-value">{{ creditLimit() | currency }}</span>
              </div>
            }
            @if (creditAvailable() != null) {
              <div class="info-item">
                <span class="info-label">Disponible</span>
                <span class="info-value">{{ creditAvailable() | currency }}</span>
              </div>
            }
            @if (isCreditBlocked()) {
              <div class="info-item">
                <span class="info-label">Crédito bloqueado</span>
                <span class="info-value">{{ creditBlockReason() || 'Sí' }}</span>
              </div>
            }
          </div>
        </app-form-section>
      }
    </div>
  }

  <app-drawer-footer>
    <button mat-button type="button" class="w-full min-[480px]:w-auto" (click)="close()">Cerrar</button>
  </app-drawer-footer>
</app-drawer>
